import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Star } from "lucide-react";
import { proxyGoogleImage } from "../lib/googleImageProxy";

interface LandingHeroSectionProps {
  businessName: string;
  tagline?: string;
  theme: any;
  heroContent?: any;
  rating?: number;
  reviewCount?: number;
  phoneNumber?: string;
  onTrackPhoneClick?: (phone: string) => void;
}

const LandingHeroSection = ({
  businessName,
  tagline,
  theme,
  heroContent,
  rating,
  reviewCount,
  phoneNumber,
  onTrackPhoneClick
}: LandingHeroSectionProps) => {
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />);
    }
    if (hasHalfStar && fullStars < 5) {
      stars.push(<Star key={fullStars} className="w-5 h-5 fill-yellow-400/50 text-yellow-400" />);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<Star key={i} className="w-5 h-5 text-gray-300" />);
    }
    return stars;
  };

  const titleSection = (
    <div
      className="py-10 lg:py-16"
      style={{
        background: heroContent?.backgroundImage
          ? 'var(--bg)'
          : `linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, transparent) 0%, var(--bg-secondary) 100%)`,
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-4 tracking-tight">
            {heroContent?.title || businessName}
          </h1>

          {/* Rating and reviews - Trust Badge Style */}
          {rating && reviewCount > 0 && (
            <div className="flex items-center justify-center mt-4">
              <div className="trust-badge">
                <div className="flex items-center">
                  {renderStars(rating)}
                </div>
                <span className="text-lg font-semibold text-gray-700">{rating.toFixed(1)}</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">{reviewCount} reviews</span>
                <span className="text-gray-400">|</span>
                <span className="text-green-600 font-medium">Verified on Google</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const descriptionSection = (
    <div className="py-8 lg:py-10 px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        {(heroContent?.description || tagline) && (
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            {heroContent?.description || tagline}
          </p>
        )}

        {/* Call Now button - Modern Style */}
        {phoneNumber && (
          <div className="flex justify-center">
            <Button
              size="lg"
              style={{
                backgroundColor: "var(--primary)",
                color: "var(--primary-foreground)"
              }}
              className="modern-button text-lg px-8 py-6 hover:opacity-90"
              onClick={() => {
                if (onTrackPhoneClick && phoneNumber) {
                  onTrackPhoneClick(phoneNumber);
                }
                window.location.href = `tel:${phoneNumber}`;
              }}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Us Today
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const bannerSection = heroContent?.backgroundImage ? (
    <div
      className="hero-banner relative overflow-hidden rounded-lg mx-4 sm:mx-6 lg:mx-8 aspect-video"
      style={{ backgroundColor: 'var(--bg-secondary)' }}
    >
      <img
        src={proxyGoogleImage(heroContent.backgroundImage, 1920)}
        alt={`${businessName} banner`}
        className="w-full h-full object-contain"
        onError={() => {
          // Fires only when the (already proxied) URL genuinely fails.
          console.error('Cover photo failed to load after proxy:', heroContent.backgroundImage);
        }}
      />
    </div>
  ) : null;

  return (
    <section id="home" className="relative">
      <div className="max-w-7xl mx-auto">
        {heroContent?.bannerFirst ? (
          <>
            {titleSection}
            {bannerSection}
            {descriptionSection}
          </>
        ) : (
          <>
            {titleSection}
            {descriptionSection}
            {bannerSection}
          </>
        )}
      </div>
    </section>
  );
};

export default LandingHeroSection;